import { createActionCreator, createReducer } from 'deox';
import { PremiumData } from '../components/header/OneTimePayAccordion';
import { AppState, PaidFeatures } from 'types/AppState';
import { createSelector } from 'reselect';
import { Scene, SceneType } from 'types/Scene';
import { Licenses } from 'types/Licenses';
import { getScenes } from './scenes';
import { PremiumItemSource } from '../constants';
import { Effects } from 'types/Effects';
import { getAudio } from './audio';
import { getEffects } from './effects';
import { Audio } from 'types/Audio';
import { CheckoutSettings } from 'types/CheckoutModal';
import { getUserPaidItems } from './oneTimePayment';
import { UserPaidItems } from 'types/UserPaidItems';
import { getCurrentTemplateId } from './template';

type CheckoutModal = AppState['checkoutModal'];

const defaultState: CheckoutModal = {
  isOpen: false,
  actionButton: null,
  settings: null,
  isSucceed: false,
  isDownloaded: true
};

export const showCheckoutModal = createActionCreator(
  'CHECKOUTMODAL/SHOW',
  resolve => (
    actionButton: CheckoutModal['actionButton'],
    premiumItems?: PremiumData[],
    settings?: CheckoutSettings
  ) => resolve({ actionButton, premiumItems, settings })
);
export const hideCheckoutModal = createActionCreator('CHECKOUTMODAL/HIDE');

export const setCheckoutSuccess = createActionCreator(
  'CHECKOUTMODAL/SUCCEES',
  resolve => (isSucceed: boolean) => resolve(isSucceed)
);

export const setPurchasedItems = createActionCreator(
  'CHECKOUTMODAL/SET_PURCHASED_ITEMS',
  resolve => (purchasedItems: CheckoutModal['purchasedItems']) => resolve(purchasedItems)
);

export const setDownloadingSuccess = createActionCreator(
  'CHECKOUTMODAL/SET_DOWNLOADING_SUCCESS',
  resolve => (isDownloaded: CheckoutModal['isDownloaded']) => resolve(isDownloaded)
);

export const checkoutModal = createReducer(defaultState, handle => [
  handle(showCheckoutModal, (state, { payload: { actionButton, premiumItems, settings } }) => ({
    isOpen: true,
    actionButton,
    premiumItems,
    purchasedItems: state.purchasedItems,
    settings: settings ?? state.settings,
    isSucceed: false,
    isDownloaded: state.isDownloaded
  })),
  handle(hideCheckoutModal, state => ({
    isOpen: false,
    actionButton: null,
    settings: state.settings,
    purchasedItems: state.purchasedItems,
    isSucceed: false,
    isDownloaded: state.isDownloaded
  })),
  handle(setCheckoutSuccess, (state, { payload: isSucceed }) => ({ ...state, isSucceed })),
  handle(setDownloadingSuccess, (state, { payload: isDownloaded }) => ({ ...state, isDownloaded })),
  handle(setPurchasedItems, (state, { payload: purchasedItems }) => ({ ...state, purchasedItems }))
]);

export const getCheckoutModal = (state: AppState): CheckoutModal => state.checkoutModal;

export const getPremiumData = createSelector<
  AppState,
  UserPaidItems[],
  Scene[],
  Effects,
  Audio,
  string,
  PremiumData[]
>(
  getUserPaidItems,
  getScenes,
  getEffects,
  getAudio,
  getCurrentTemplateId,
  (userPaidItems, sceneItems, effects, audio, currentTemplateId) => {
    const usedPicsartImages: PremiumData[] = sceneItems
      .filter(
        scene =>
          scene.license === Licenses.PhotoChooserPremium &&
          scene.type === SceneType.IMAGE &&
          scene.source === PremiumItemSource.Picsart
      )
      .map(scene => ({
        id: scene.sourceId,
        src: scene.src,
        type: PaidFeatures.PhotoChooser,
        source: PremiumItemSource.Picsart
      }));

    const usedUnsplashImages: PremiumData[] = sceneItems
      .filter(
        scene =>
          scene.license === Licenses.PhotoChooserPremium &&
          scene.type === SceneType.IMAGE &&
          scene.source === PremiumItemSource.Unsplash
      )
      .map(scene => ({
        id: scene.sourceId,
        src: scene.src,
        type: PaidFeatures.PhotoChooser,
        source: PremiumItemSource.Unsplash
      }));

    const usedVideos: PremiumData[] = sceneItems
      .filter(
        scene =>
          scene.license === Licenses.PhotoChooserPremium &&
          scene.type === SceneType.VIDEO &&
          scene.source === PremiumItemSource.Picsart
      )
      .map(scene => ({
        id: scene.sourceId,
        src: scene.src,
        type: PaidFeatures.VideoChooser,
        source: PremiumItemSource.Picsart
      }));

    const userPaidItemsIds = userPaidItems.map(item => item.id);

    const usedShutterstockImages: PremiumData[] = sceneItems
      .filter(
        scene =>
          scene.license === Licenses.PhotoChooserPremium &&
          scene.source === PremiumItemSource.Shutterstock &&
          scene.type === SceneType.IMAGE &&
          !userPaidItemsIds.includes(scene.sourceId)
      )
      .map(scene => ({
        id: scene.sourceId,
        src: scene.src,
        type: PaidFeatures.PhotoChooser,
        source: PremiumItemSource.Shutterstock
      }));

    const usedShutterstockVideos: PremiumData[] = sceneItems
      .filter(
        scene =>
          scene.license === Licenses.PhotoChooserPremium &&
          scene.source === PremiumItemSource.Shutterstock &&
          scene.type === SceneType.VIDEO &&
          !userPaidItemsIds.includes(scene.sourceId)
      )
      .map(scene => ({
        id: scene.sourceId,
        src: scene.src,
        type: PaidFeatures.VideoChooser,
        source: PremiumItemSource.Shutterstock
      }));

    const usedTextPresets: PremiumData[] = Object.values(effects)
      .flat()
      .reduce((prev, curr) => {
        if (!prev.includes(curr.presetId) && curr.presetId) {
          prev.push(curr.presetId);
        }
        return prev;
      }, [])
      .map(id => ({
        id,
        type: PaidFeatures.Font,
        source: PremiumItemSource.Picsart
      }));

    const usedTransitions: PremiumData[] = sceneItems
      .reduce((prev, curr) => {
        if (!prev.includes(curr.transition.id) && curr.transition.is_paid === true) {
          prev.push(curr.transition.id);
        }
        return prev;
      }, [])
      .map(id => ({
        id,
        type: PaidFeatures.Transition,
        source: PremiumItemSource.Picsart
      }));

    const usedAudio: PremiumData = audio?.isPaid &&
      audio.id && {
        id: audio.id,
        type: PaidFeatures.Audio,
        source: PremiumItemSource.Picsart
      };

    const usedTemplate: PremiumData = sceneItems.find(
      scene => scene.license && scene.license === Licenses.TemplatePremium
    ) && {
      id: currentTemplateId,
      type: PaidFeatures.Template,
      source: PremiumItemSource.Picsart
    };

    const usedFonts: PremiumData[] = Object.values(effects)
      .flat()
      .reduce((prev, curr) => {
        if (!prev.includes(curr.font.src) && curr.font && curr.font.isPaid && !curr.presetId) {
          prev.push(curr.font.src);
        }
        return prev;
      }, [])
      .map(id => ({
        id,
        type: PaidFeatures.Font,
        source: PremiumItemSource.Picsart
      }));

    const premiumData = [
      ...usedPicsartImages,
      ...usedUnsplashImages,
      ...usedShutterstockImages,
      ...usedVideos,
      ...usedTextPresets,
      ...usedTransitions,
      ...usedFonts,
      ...usedShutterstockVideos
    ];

    usedAudio && premiumData.push(usedAudio);
    usedTemplate && premiumData.push(usedTemplate);

    return premiumData;
  }
);
